import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Calendar as CalendarIcon, Send, MessageCircle, Mail, Instagram } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { useI18n, CONTACT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Booking({ requestedService }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    service: "",
    message: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (requestedService && requestedService.value) {
      setForm((f) => ({ ...f, service: requestedService.value }));
    }
  }, [requestedService]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.location || !form.service) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/booking`, {
        ...form,
        date: date ? format(date, "yyyy-MM-dd") : null,
      });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", location: "", service: "", message: "" });
      setDate(null);
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  const services = t.services.list.map((s) => s.title);

  return (
    <section
      id="contact"
      className="bg-ink-900 text-sand-50 py-24 md:py-32 lg:py-40"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5"
        >
          <span className="block text-[10px] md:text-xs tracking-[0.4em] uppercase text-gold mb-6">
            — {t.contact.kicker}
          </span>
          <h2
            className="font-serif font-light leading-[1.05] tracking-tight text-3xl md:text-5xl lg:text-[56px] mb-7"
            data-testid="contact-title"
          >
            {t.contact.title}
          </h2>
          <p className="text-[15px] md:text-base text-sand-50/65 leading-[1.75] max-w-[400px] mb-12">
            {t.contact.sub}
          </p>

          <div className="flex flex-col gap-3 mt-8">
            <a
              href={`https://wa.me/${CONTACT.whatsappRaw}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-between gap-4 border border-sand-50/20 px-5 py-3.5 hover:border-gold hover:bg-gold/5 transition-all duration-500"
              data-testid="contact-whatsapp-link"
            >
              <span className="flex items-center gap-3">
                <MessageCircle size={16} strokeWidth={1.2} className="text-gold" />
                <span className="text-[10.5px] tracking-[0.3em] uppercase text-sand-50/85">{t.contact.whatsapp}</span>
              </span>
              <span className="text-[12px] tracking-wide text-sand-50/55 group-hover:text-gold transition-colors">
                {CONTACT.whatsappDisplay}
              </span>
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              className="group inline-flex items-center justify-between gap-4 border border-sand-50/20 px-5 py-3.5 hover:border-gold hover:bg-gold/5 transition-all duration-500"
              data-testid="contact-email-link"
            >
              <span className="flex items-center gap-3">
                <Mail size={16} strokeWidth={1.2} className="text-gold" />
                <span className="text-[10.5px] tracking-[0.3em] uppercase text-sand-50/85">{t.contact.emailUs}</span>
              </span>
              <span className="text-[12px] tracking-wide text-sand-50/55 group-hover:text-gold transition-colors truncate max-w-[220px]">
                {CONTACT.email}
              </span>
            </a>
            <a
              href={CONTACT.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-between gap-4 border border-sand-50/20 px-5 py-3.5 hover:border-gold hover:bg-gold/5 transition-all duration-500"
              data-testid="contact-instagram-link"
            >
              <span className="flex items-center gap-3">
                <Instagram size={16} strokeWidth={1.2} className="text-gold" />
                <span className="text-[10.5px] tracking-[0.3em] uppercase text-sand-50/85">{t.contact.instagram}</span>
              </span>
              <span className="text-[12px] tracking-wide text-sand-50/55 group-hover:text-gold transition-colors">
                {CONTACT.instagram}
              </span>
            </a>
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10"
          data-testid="booking-form"
          noValidate
        >
          <Field label={t.contact.name} required>
            <input
              type="text"
              required
              value={form.name}
              onChange={update("name")}
              data-testid="booking-name-input"
              className="w-full bg-transparent border-b border-sand-50/20 py-3 text-[15px] focus:outline-none focus:border-gold placeholder:text-sand-50/35 placeholder:font-light transition-colors duration-300"
            />
          </Field>
          <Field label={t.contact.email} required>
            <input
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              data-testid="booking-email-input"
              className="w-full bg-transparent border-b border-sand-50/20 py-3 text-[15px] focus:outline-none focus:border-gold placeholder:text-sand-50/35 placeholder:font-light transition-colors duration-300"
            />
          </Field>
          <Field label={t.contact.phone} required>
            <input
              type="tel"
              required
              value={form.phone}
              onChange={update("phone")}
              data-testid="booking-phone-input"
              className="w-full bg-transparent border-b border-sand-50/20 py-3 text-[15px] focus:outline-none focus:border-gold placeholder:text-sand-50/35 placeholder:font-light transition-colors duration-300"
            />
          </Field>
          <Field label={t.contact.location} required>
            <input
              type="text"
              required
              value={form.location}
              onChange={update("location")}
              data-testid="booking-location-input"
              className="w-full bg-transparent border-b border-sand-50/20 py-3 text-[15px] focus:outline-none focus:border-gold placeholder:text-sand-50/35 placeholder:font-light transition-colors duration-300"
            />
          </Field>

          <Field label={t.contact.service} required>
            <div className="relative">
              <select
                required
                value={form.service}
                onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
                data-testid="booking-service-trigger"
                className={`w-full bg-transparent border-b border-sand-50/20 py-3 pr-8 text-[15px] focus:outline-none focus:border-gold transition-colors duration-300 appearance-none cursor-pointer ${
                  form.service ? "text-sand-50" : "text-sand-50/35"
                }`}
              >
                <option value="" disabled className="text-ink-900">
                  {t.contact.servicePlaceholder}
                </option>
                {form.service && !services.includes(form.service) && (
                  <option value={form.service} className="text-ink-900">
                    {form.service}
                  </option>
                )}
                {services.map((s) => (
                  <option
                    key={s}
                    value={s}
                    className="text-ink-900"
                    data-testid={`booking-service-option-${s}`}
                  >
                    {s}
                  </option>
                ))}
              </select>
              <CalendarIcon className="hidden" />
              <svg
                className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-sand-50/50"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </Field>

          <Field label={t.contact.date}>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="booking-date-trigger"
                  className="w-full flex items-center justify-between gap-3 bg-transparent border-b border-sand-50/20 py-3 text-[15px] text-sand-50 hover:border-sand-50/40 focus:outline-none focus:border-gold transition-colors duration-300"
                >
                  <span className={date ? "" : "text-sand-50/35"}>
                    {date ? format(date, "dd MMM yyyy") : "—"}
                  </span>
                  <CalendarIcon size={14} strokeWidth={1.2} className="text-sand-50/50" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="bg-sand-50 text-ink-900 border-sand-200 rounded-none p-0"
              >
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </Field>

          <Field label={t.contact.message} className="md:col-span-2">
            <textarea
              rows={3}
              value={form.message}
              onChange={update("message")}
              placeholder={t.contact.messagePlaceholder}
              data-testid="booking-message-input"
              className="w-full bg-transparent border-b border-sand-50/20 py-3 text-[15px] focus:outline-none focus:border-gold placeholder:text-sand-50/35 placeholder:font-light placeholder:italic transition-colors duration-300 resize-none"
            />
          </Field>

          <div className="md:col-span-2 mt-6">
            <button
              type="submit"
              disabled={submitting}
              className="group relative inline-flex items-center justify-center gap-3 bg-sand-50 text-ink-900 px-10 py-4 text-[10.5px] uppercase tracking-[0.35em] hover:bg-gold transition-all duration-500 disabled:opacity-60 overflow-hidden"
              data-testid="booking-submit-button"
            >
              <span>{submitting ? t.contact.sending : t.contact.send}</span>
              <Send size={14} strokeWidth={1.2} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <label className={`flex flex-col gap-3 ${className}`}>
      <span className="text-[9.5px] tracking-[0.35em] uppercase text-sand-50/55">
        {label}
        {required && <span className="text-gold/70 ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
